import type {
  BookingData,
  BookingEmployee,
  BookingService,
  DayAvailability,
  DayHours,
  LunchBreak,
  SlotInfo,
  WeeklyWindow,
} from "./types";

export const BOOKING_TIMEZONE = "Europe/Berlin";

/**
 * Default hours when a day has no persisted opening hours.
 * Mon–Sat: 09:00–18:00 open; Sunday: closed.
 * Matches Flutter `defaultOpeningHoursForWeekday`.
 */
export const DEFAULT_OPEN_DAY_HOURS: DayHours = {
  closed: false,
  open: "09:00",
  close: "18:00",
};

export const DEFAULT_CLOSED_DAY_HOURS: DayHours = {
  closed: true,
  open: "09:00",
  close: "18:00",
};

/** @deprecated Prefer defaultHoursForWeekday — kept for open-time fallbacks. */
export const DEFAULT_DAY_HOURS = DEFAULT_OPEN_DAY_HOURS;

export function defaultHoursForWeekday(key: string): DayHours {
  return key === "sun" ? DEFAULT_CLOSED_DAY_HOURS : DEFAULT_OPEN_DAY_HOURS;
}

export function defaultHoursForDateKey(dateKey: string): DayHours {
  return defaultHoursForWeekday(weekdayKeyFromDate(berlinDayStartUtc(dateKey)));
}

export function berlinDateKey(d: Date): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: BOOKING_TIMEZONE }).format(d);
}

export function parseHmToMinutes(v: string): number {
  const parts = v.split(":");
  const h = Number(parts[0] ?? 9);
  const m = Number(parts[1] ?? 0);
  return h * 60 + m;
}

function weekdayKeyFromDate(d: Date): string {
  const weekday = new Intl.DateTimeFormat("en-US", {
    timeZone: BOOKING_TIMEZONE,
    weekday: "short",
  }).format(d);
  const map: Record<string, string> = {
    Mon: "mon",
    Tue: "tue",
    Wed: "wed",
    Thu: "thu",
    Fri: "fri",
    Sat: "sat",
    Sun: "sun",
  };
  return map[weekday] ?? "mon";
}

function berlinMinutes(d: Date): number {
  const hour = Number(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: BOOKING_TIMEZONE,
      hour: "numeric",
      hour12: false,
    }).format(d)
  );
  const minute = Number(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: BOOKING_TIMEZONE,
      minute: "numeric",
    }).format(d)
  );
  return hour * 60 + minute;
}

export function berlinDayStartUtc(dateKey: string): Date {
  const [y, m, d] = dateKey.split("-").map(Number);
  const utcMidnight = Date.UTC(y, m - 1, d, 0, 0, 0);
  for (let offsetHours = -2; offsetHours <= 2; offsetHours++) {
    const probe = new Date(utcMidnight + offsetHours * 3_600_000);
    if (berlinDateKey(probe) === dateKey) {
      const hour = Number(
        new Intl.DateTimeFormat("en-GB", {
          timeZone: BOOKING_TIMEZONE,
          hour: "numeric",
          hour12: false,
        }).format(probe)
      );
      if (hour === 0) return probe;
    }
  }
  return new Date(utcMidnight - 3_600_000);
}

export function berlinLocalToDate(dateKey: string, minutesFromMidnight: number): Date {
  const dayStart = berlinDayStartUtc(dateKey);
  return new Date(dayStart.getTime() + minutesFromMidnight * 60_000);
}

function resolveDayHours(
  dateKey: string,
  settings: BookingData["settings"],
  employee?: BookingEmployee | null
): DayHours | null {
  const weekdayKey = weekdayKeyFromDate(berlinDayStartUtc(dateKey));
  return (
    employee?.exceptionDays[dateKey] ??
    settings.exceptionDays[dateKey] ??
    employee?.regularSchedule[weekdayKey] ??
    settings.openingHours[weekdayKey] ??
    null
  );
}

function resolveLunchBreak(
  dateKey: string,
  settings: BookingData["settings"],
  employee?: BookingEmployee | null
): { start: Date | null; end: Date | null } {
  const lb: LunchBreak | null = employee?.lunchBreak ?? settings.lunchBreak;
  if (!lb) return { start: null, end: null };
  const lunchMin = parseHmToMinutes(lb.start);
  const lunchStart = berlinLocalToDate(dateKey, lunchMin);
  return {
    start: lunchStart,
    end: new Date(lunchStart.getTime() + lb.durationMinutes * 60_000),
  };
}

function timeRangesOverlap(aStart: Date, aEnd: Date, bStart: Date, bEnd: Date): boolean {
  return aStart < bEnd && bStart < aEnd;
}

const WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

/**
 * Converts weekly opening hours into WeeklyWindow entries (Mon–Sun).
 * Days without a persisted entry fall back to defaultHoursForWeekday;
 * closed days (including default Sunday) are skipped.
 */
export function openingHoursToWeeklyWindows(
  settings: BookingData["settings"],
  employee?: BookingEmployee | null
): WeeklyWindow[] {
  const windows: WeeklyWindow[] = [];
  for (let i = 0; i < WEEKDAY_KEYS.length; i++) {
    const key = WEEKDAY_KEYS[i];
    const hours =
      employee?.regularSchedule[key] ??
      settings.openingHours[key] ??
      defaultHoursForWeekday(key);
    if (hours.closed) continue;
    windows.push({
      weekday: i + 1,
      startMinutes: parseHmToMinutes(hours.open ?? DEFAULT_OPEN_DAY_HOURS.open!),
      endMinutes: parseHmToMinutes(hours.close ?? DEFAULT_OPEN_DAY_HOURS.close!),
    });
  }
  return windows;
}

/**
 * Configured service windows if present; otherwise opening-hours fallback.
 * Mirrors Flutter `effectiveServiceWindows` / `effectivePickupWindows`.
 */
export function effectiveServiceWindows(
  employee: BookingEmployee,
  settings: BookingData["settings"],
  serviceId: string
): WeeklyWindow[] {
  const configured = employee.serviceWindows[serviceId] ?? [];
  if (configured.length > 0) return configured;
  return openingHoursToWeeklyWindows(settings, employee);
}

function unionServiceWindows(
  employees: BookingEmployee[],
  settings: BookingData["settings"],
  serviceId: string
): WeeklyWindow[] {
  const merged: WeeklyWindow[] = [];
  for (const employee of employees) {
    merged.push(...effectiveServiceWindows(employee, settings, serviceId));
  }
  return merged;
}

function berlinWeekday(d: Date): number {
  const weekday = new Intl.DateTimeFormat("en-US", {
    timeZone: BOOKING_TIMEZONE,
    weekday: "short",
  }).format(d);
  const map: Record<string, number> = {
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
    Sun: 7,
  };
  return map[weekday] ?? 1;
}

function slotWithinServiceWindows(
  slotStart: Date,
  slotEnd: Date,
  serviceWindows: WeeklyWindow[]
): boolean {
  if (serviceWindows.length === 0) return true;
  const weekday = berlinWeekday(slotStart);
  const startMin = berlinMinutes(slotStart);
  const endMin = berlinMinutes(slotEnd);
  return serviceWindows.some(
    (w) => w.weekday === weekday && startMin >= w.startMinutes && endMin <= w.endMinutes
  );
}

function bookableEmployeesForService(
  data: BookingData,
  service: BookingService
): BookingEmployee[] {
  let staff = data.employees;
  if (service.bookableByEmployeeIds.length > 0) {
    staff = staff.filter((e) => service.bookableByEmployeeIds.includes(e.id));
  }
  return staff;
}

function employeeAvailableAt(
  employee: BookingEmployee,
  service: BookingService,
  dateKey: string,
  slotStart: Date,
  slotEnd: Date,
  settings: BookingData["settings"],
  availability: DayAvailability
): boolean {
  const windows = effectiveServiceWindows(employee, settings, service.id);
  if (!slotWithinServiceWindows(slotStart, slotEnd, windows)) {
    return false;
  }

  const lunch = resolveLunchBreak(dateKey, settings, employee);
  if (
    lunch.start &&
    lunch.end &&
    timeRangesOverlap(slotStart, slotEnd, lunch.start, lunch.end)
  ) {
    return false;
  }

  const apptConflict = availability.appointments.some(
    (a) =>
      a.status !== "canceled" &&
      a.employeeId === employee.id &&
      timeRangesOverlap(slotStart, slotEnd, new Date(a.startAt), new Date(a.endAt))
  );
  if (apptConflict) return false;

  const blockConflict = availability.blocks.some(
    (b) =>
      (!b.employeeId || b.employeeId === employee.id) &&
      timeRangesOverlap(slotStart, slotEnd, new Date(b.startAt), new Date(b.endAt))
  );
  return !blockConflict;
}

export function buildSlots(params: {
  data: BookingData;
  service: BookingService;
  dateKey: string;
  availability: DayAvailability;
  selectedEmployee: BookingEmployee | null;
  noPreference: boolean;
}): SlotInfo[] {
  const { data, service, dateKey, availability, selectedEmployee, noPreference } = params;
  const settings = data.settings;
  const resolved = resolveDayHours(dateKey, settings, selectedEmployee);
  // Explicitly closed days stay empty; missing hours fall back to the app default.
  if (resolved?.closed === true) return [];
  const hours = resolved ?? defaultHoursForDateKey(dateKey);
  if (hours.closed) return [];

  const openHour = parseHmToMinutes(hours.open ?? DEFAULT_OPEN_DAY_HOURS.open!);
  const closeHour = parseHmToMinutes(hours.close ?? DEFAULT_OPEN_DAY_HOURS.close!);
  const now = new Date();
  const slots: SlotInfo[] = [];

  const bookableEmployees = noPreference
    ? bookableEmployeesForService(data, service)
    : selectedEmployee
      ? [selectedEmployee]
      : [];

  const serviceWindows =
    noPreference && bookableEmployees.length > 0
      ? unionServiceWindows(bookableEmployees, settings, service.id)
      : selectedEmployee
        ? effectiveServiceWindows(selectedEmployee, settings, service.id)
        : [];

  const lunch =
    !noPreference && selectedEmployee
      ? resolveLunchBreak(dateKey, settings, selectedEmployee)
      : { start: null, end: null };

  let cursorMin = openHour;
  const leadHours =
    typeof settings.bookingLeadTimeHours === "number"
      ? Math.max(0, settings.bookingLeadTimeHours)
      : 2;
  const cutoff = new Date(now.getTime() + leadHours * 3_600_000);

  while (cursorMin + service.durationMinutes <= closeHour) {
    const slotStart = berlinLocalToDate(dateKey, cursorMin);
    const slotEnd = new Date(slotStart.getTime() + service.durationMinutes * 60_000);

    if (serviceWindows.length > 0 && !slotWithinServiceWindows(slotStart, slotEnd, serviceWindows)) {
      cursorMin += service.durationMinutes;
      continue;
    }

    if (!(slotEnd > now)) {
      cursorMin += service.durationMinutes;
      continue;
    }
    if (leadHours > 0 && slotStart < cutoff) {
      cursorMin += service.durationMinutes;
      continue;
    }

    let available = false;
    if (noPreference) {
      available = bookableEmployees.some((employee) =>
        employeeAvailableAt(
          employee,
          service,
          dateKey,
          slotStart,
          slotEnd,
          settings,
          availability
        )
      );
    } else if (selectedEmployee) {
      const lunchOverlap =
        lunch.start &&
        lunch.end &&
        timeRangesOverlap(slotStart, slotEnd, lunch.start, lunch.end);
      if (!lunchOverlap) {
        available = employeeAvailableAt(
          selectedEmployee,
          service,
          dateKey,
          slotStart,
          slotEnd,
          settings,
          availability
        );
      }
    }

    slots.push({ startAt: slotStart, endAt: slotEnd, available });
    cursorMin += service.durationMinutes;
  }

  return slots;
}

export function formatSlotTime(d: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: BOOKING_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

export function formatSlotDate(d: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: BOOKING_TIMEZONE,
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(d);
}

export function formatPrice(cents: number | null, currency: string, locale: string): string | null {
  if (cents == null) return null;
  return new Intl.NumberFormat(locale, { style: "currency", currency }).format(cents / 100);
}

/** Formats a quantity, trimming trailing zeros (e.g. 2.5 → "2,5" in de). */
export function formatQty(qty: number, locale: string): string {
  if (!Number.isFinite(qty)) return "1";
  if (Number.isInteger(qty)) return String(qty);
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(qty);
}

/** Short UN/ECE unit label for order lines (de-biased defaults). */
export function unitLabelShort(code: string | null | undefined, locale = "de"): string {
  const c = (code ?? "C62").toUpperCase();
  const de: Record<string, string> = {
    C62: "Stk.",
    HUR: "Std.",
    MIN: "Min.",
    DAY: "Tag",
    KMT: "km",
    MTR: "m",
    MTK: "m²",
    LTR: "l",
    KGM: "kg",
  };
  const en: Record<string, string> = {
    C62: "pcs",
    HUR: "h",
    MIN: "min",
    DAY: "day",
    KMT: "km",
    MTR: "m",
    MTK: "m²",
    LTR: "l",
    KGM: "kg",
  };
  const map = locale.startsWith("de") ? de : en;
  return map[c] ?? de.C62;
}

export function nextDays(count: number): string[] {
  const days: string[] = [];
  const today = berlinDayStartUtc(berlinDateKey(new Date()));
  for (let i = 0; i < count; i++) {
    const d = new Date(today.getTime() + i * 24 * 3_600_000);
    days.push(berlinDateKey(d));
  }
  return days;
}
